import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, GraduationCap, Wand2, Clock, ListChecks, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api } from "@/lib/api";
import QuizRunner from "@/components/QuizRunner";

const COUNTS = [10, 15, 20];
const MINUTES = [10, 15, 20];

export default function ExamMode() {
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [count, setCount] = useState(10);
  const [minutes, setMinutes] = useState(15);
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.getExamAttempts().then(setHistory).catch(() => {});

  useEffect(() => {
    api.getSubjects().then((s) => { setSubjects(s); setSelected(s.map((x) => x.id)); }).catch(() => {});
    loadHistory();
  }, []);

  const selectedNames = useMemo(
    () => subjects.filter((s) => selected.includes(s.id)).map((s) => s.name),
    [subjects, selected]
  );

  const onComplete = async ({ score, total, duration }) => {
    try {
      await api.saveExamAttempt({ score, total, duration_seconds: duration, question_count: count, minutes, subject_names: selectedNames });
      loadHistory();
      toast.success(`Esame salvato: ${score}/${total}`);
    } catch { /* silent */ }
  };

  const toggle = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const start = async () => {
    if (selected.length === 0) { toast.error("Seleziona almeno una materia."); return; }
    setLoading(true);
    try {
      const res = await api.examQuiz(count, selected.length === subjects.length ? null : selected);
      if (!res.questions?.length) { toast.error("Nessuna domanda generata."); return; }
      setQuestions(res.questions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione dell'esame.");
    } finally { setLoading(false); }
  };

  const countdown = useMemo(() => minutes * 60, [minutes]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      <Link to="/" data-testid="exam-back-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Torna ai materiali
      </Link>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-center gap-2 mb-6">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-secondary-foreground">
            <GraduationCap className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight leading-none">Modalità Esame</h1>
            <p className="text-sm text-muted-foreground mt-1">Simulazione a tempo con domande da più materie</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
          {questions ? (
            <QuizRunner questions={questions} timer countdown={countdown} onComplete={onComplete} onRestart={() => {}} />
          ) : (
            <div className="space-y-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Materie incluse</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {subjects.map((s) => (
                    <label key={s.id} data-testid={`exam-subject-${s.id}`} className="flex items-center gap-2.5 rounded-xl border border-border p-3 cursor-pointer hover:bg-accent/40 transition-colors">
                      <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} />
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-sm font-medium truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Domande</div>
                  <div className="flex gap-2">
                    {COUNTS.map((c) => (
                      <button key={c} data-testid={`exam-count-${c}`} onClick={() => setCount(c)} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${count === c ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}>{c}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Minuti</div>
                  <div className="flex gap-2">
                    {MINUTES.map((m) => (
                      <button key={m} data-testid={`exam-minutes-${m}`} onClick={() => setMinutes(m)} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${minutes === m ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}>{m}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-1">
                <Button data-testid="start-exam-button" onClick={start} disabled={loading} className="w-full rounded-xl gap-2 h-11">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {loading ? "Preparo l'esame..." : `Inizia esame (${count} domande · ${minutes} min)`}
                </Button>
              </div>
            </div>
          )}
        </div>

        {!questions && history.length > 0 && (
          <div className="mt-6" data-testid="exam-history">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
              <History className="h-3.5 w-3.5" /> Storico esami ({history.length})
            </div>
            <div className="space-y-2">
              {history.map((h) => {
                const pct = Math.round((h.score / h.total) * 100);
                return (
                  <div key={h.id} className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl p-3.5 text-sm" data-testid={`exam-attempt-${h.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{h.subject_names?.length ? h.subject_names.join(", ") : "Tutte le materie"}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(h.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {h.question_count} domande
                        {h.duration_seconds > 0 && ` · ${Math.floor(h.duration_seconds / 60)}:${String(h.duration_seconds % 60).padStart(2, "0")}`}
                      </p>
                    </div>
                    <span className={`font-display font-bold text-lg shrink-0 ${pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-destructive"}`}>{h.score}/{h.total}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
